<template>
    <ul>
      <li v-for="milestone in milestoneStates" class="milestone-item" :key="milestone.key">
        <div class="milestone-item-icon">
        <font-awesome-icon class="milestone-flag-indicator" icon="circle" :color="milestone.color"/>
        </div>
        <div class="milestone-item-content content">
          <div class="">
            {{ milestone.displayName }}
          </div>
        </div>
      </li>
    </ul>
</template>

<script>
import MILESTONES_TO_TRACK from '@/lookup-data/milestones-to-track'
import { mapState, mapActions } from 'vuex'

const REFRESH_TIMER_IN_SECONDS = 60

export default {
  name: 'MileStones',

  props: {
    // TODO: this is supposed to be temporary until a legit "job-certified" execution log entry is put in place
    isJobCertified: {
      type: Boolean,
      default: true
    },
    companyJobKey: {
      type: String,
      required: true
    }
  },

  mounted() {
    if (!this.executionLog.length) {
      this.fetchExecutionLogByKey(this.companyJobKey)
    }

    this.startRefreshTimer()
  },

  beforeDestroy() {
    this.endRefreshTimer()
  },

  data() {
    return {
      refreshTimerId: null
    }
  },

  computed: {
    ...mapState('companyJobs', ['executionLogs']),

    executionLog() {
      return this.executionLogs[this.companyJobKey] || []
    },

    /******
     Everything under this comment is duplicated from `TransactionsDashboard-row.
     Only exception is the `.reverse()` in `milestoneStates`
     *********/

    /**
     * An array of all milestones with a flag to indicate if they have been executed, skipped or neither.
     * Leverages `_allExecutedMilestones` and adds information about if a milestone was skipped or not.
     *
     */
    milestoneStates() {
      // get array of true/false flags... then find the LAST `true` entry.
      const wasExecutedFlags = this._allExecutedMilestones.map((milestone) => milestone.wasExecuted)
      const indexOfLastExecutedMilestone = wasExecutedFlags.lastIndexOf(true)
      // every index BEFORE `indexOfLastExecutedMilestone` should have been executed, otherwise they were skipped
      return this._allExecutedMilestones
        .map((milestone, index) => {
          let wasSkipped = index < indexOfLastExecutedMilestone && !milestone.wasExecuted
          // TODO: this is supposed to be temporary until a legit "job-certified" execution log entry is put in place
          if (index <= indexOfLastExecutedMilestone && milestone.key === 'finish_job' && !this.isJobCertified) {
            wasSkipped = true
          }
          return {
            ...milestone,
            wasSkipped,
            color: wasSkipped ? 'red' : milestone.color
          }
        })
        .reverse()
    },

    /**
     * An array of all milestones with a flag to indicate if they have been executed or not.
     * The order in which tye were executed does not matter, all that matters is that the key appears in the execution
     * log.
     *
     * This is not intended to be used directly but is a support  mechanism to aid the calculation of `milestoneStates`
     */
    _allExecutedMilestones() {
      return MILESTONES_TO_TRACK.map((milestone) => {
        const milestoneKey = milestone.key
        const wasExecuted = this.executedMilestoneKeys.includes(milestoneKey)
        return {
          key: milestoneKey,
          recordedValue: wasExecuted ? this.getEntryValue(milestoneKey) : null,
          displayName: milestone.displayName,
          wasExecuted,
          color: wasExecuted ? 'green' : 'gray'
        }
      })
    },

    executedMilestoneKeys() {
      return this.executionLog.map((entry) => entry.companyJobExecutionLogEntryKey)
    }
  },

  methods: {
    ...mapActions('companyJobs', ['fetchExecutionLogByKey']),

    startRefreshTimer() {
      const FETCH_TIMER = REFRESH_TIMER_IN_SECONDS * 1000
      this.refreshTimerId = window.setInterval(async () => {
        await this.fetchExecutionLogByKey(this.companyJobKey)
      }, FETCH_TIMER)
    },

    endRefreshTimer() {
      window.clearInterval(this.refreshTimerId)
    },

    getEntryValue(entryKey) {
      const entry = this.executionLog.find((entry) => entry.companyJobExecutionLogEntryKey === entryKey)
      let value = null
      if (entry) {
        value = entry.companyJobExecutionLogEntryValue
      }

      return value
    }
  }
}
</script>

<style scoped lang="scss">
.milestone-item {
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  align-items: center;

  .milestone-item-content {
    margin-left: 8px;
  }
}
</style>
